/**
 * Local/session storage compatibility layer.
 *
 * We migrated all storage keys from ``patripilot_*`` to ``fonbex_*``. To
 * preserve user preferences across the rebrand rollout, this helper reads
 * the new key first, then transparently migrates any legacy key found.
 * After first read, only ``fonbex_*`` is written.
 */
const LEGACY_MAP = {
  "fonbex_org": "patripilot_org",
  "fonbex_onboarding_skipped_org": "patripilot_onboarding_skipped_org",
  "fonbex_onboarding_skipped_at": "patripilot_onboarding_skipped_at",
  "fonbex_fab_minimized": "patripilot_fab_minimized",
  "fonbex_email_banner_dismissed": "patripilot_email_banner_dismissed",
  "fonbex_email_last_sent": "patripilot_email_last_sent",
};

const pickStorage = (kind) =>
  (typeof window !== "undefined" && window[kind]) || null;

const readWithFallback = (kind, key) => {
  const store = pickStorage(kind);
  if (!store) return null;
  const direct = store.getItem(key);
  if (direct !== null) return direct;
  const legacy = LEGACY_MAP[key];
  if (!legacy) return null;
  const value = store.getItem(legacy);
  if (value !== null) {
    // migrate transparently
    try {
      store.setItem(key, value);
      store.removeItem(legacy);
    } catch {
      /* ignore quota / privacy errors */
    }
  }
  return value;
};

const writeToStore = (kind, key, value) => {
  const store = pickStorage(kind);
  if (!store) return;
  try {
    if (value === null || value === undefined) {
      store.removeItem(key);
    } else {
      store.setItem(key, String(value));
    }
    const legacy = LEGACY_MAP[key];
    if (legacy) store.removeItem(legacy);
  } catch {
    /* ignore */
  }
};

export const localStore = {
  get: (key) => readWithFallback("localStorage", key),
  set: (key, value) => writeToStore("localStorage", key, value),
  remove: (key) => writeToStore("localStorage", key, null),
};

export const sessionStore = {
  get: (key) => readWithFallback("sessionStorage", key),
  set: (key, value) => writeToStore("sessionStorage", key, value),
  remove: (key) => writeToStore("sessionStorage", key, null),
};
